import { useState } from 'react';
import { Confirm, SaveButton, Toolbar } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Button, Grid } from '@mui/material';

import { CustomDeleteButton } from '@app/components/common/CustomDeleteButton';
import { DeleteConfirmation } from '@app/components/common/Table/DefaultTable/types';

interface DefaultFormToolbarProps {
  resourceSingular: string;
  hideDelete?: boolean;
  customDiscardValidation?(): boolean;
  resolveDeleteConfirmation?(resourceId: number): Promise<DeleteConfirmation>;
  onClose?(): void;
}

export const DefaultFormToolbar = ({
  onClose,
  customDiscardValidation,
  resourceSingular,
  resolveDeleteConfirmation,
  hideDelete,
}: DefaultFormToolbarProps) => {
  const navigate = useNavigate();

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);

  const { formState } = useFormContext();

  const goBackOnePage = () => {
    navigate(-1);
  };

  const onCancel = () => {
    onClose ? onClose() : goBackOnePage();
  };

  const handleDialogToggle = (bool: boolean) => {
    setIsCancelConfirmationOpen(bool);
  };

  const handleClickDiscard = () => {
    let shouldShowDialog = false;
    if (customDiscardValidation) {
      const validationResult = customDiscardValidation();
      shouldShowDialog = formState.isDirty || !validationResult;
    } else {
      shouldShowDialog = formState.isDirty;
    }

    shouldShowDialog ? handleDialogToggle(true) : onCancel();
  };

  return (
    <Toolbar style={{ minHeight: 50 }}>
      <Confirm
        isOpen={isCancelConfirmationOpen}
        title={`Discard changes`}
        content={`Your changes have not been saved. Are you sure you want to close without saving?`}
        onConfirm={onCancel}
        cancel="Back to edit"
        confirm="Close without saving"
        onClose={() => handleDialogToggle(false)}
      />

      <Grid
        spacing={2}
        xl={8}
        lg={8}
        md={12}
        xs={12}
        container
        item
        sx={{ alignItems: 'center', marginTop: 0, marginLeft: 0 }}>
        <div style={{ flex: 1 }}>
          <SaveButton label="Save" alwaysEnable />
          <Button
            style={{ marginLeft: 20, marginRight: 20, flex: 1 }}
            variant="text"
            color="primary"
            onClick={handleClickDiscard}
            data-cy="btn-close-without-saving">
            CLOSE WITHOUT SAVING
          </Button>
        </div>
        {!hideDelete && (
          <CustomDeleteButton
            resourceSingular={resourceSingular}
            resolveDeleteConfirmation={resolveDeleteConfirmation}
          />
        )}
      </Grid>
    </Toolbar>
  );
};
